import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { connect } from 'pwa-helpers/connect-mixin';
import { Generation } from '@api-consent/types/common';
import { LitElementExtended } from '@src/classes/LitElementExtended';
import './tef-coms-consent';
import { service } from '@src/service';
import {
  TRACKER_EVENT_NAME_START_CHANGE_PERMISSION,
  TRACKER_EVENT_NAME_CLICK_COLLAPSABLE,
} from '@src/service/tracker';
import { store } from '@src/store';
import {
  deny, oneclick, setActiveGroupInd, setCollapsibleOpen,
} from '@src/store/consents';
import { ConsentGroup } from '@src/types/common';
import { haveUncheckedFeatures } from '@src/util/consents';

@customElement('tef-coms-consent-group')
export class TefComsConsentGroup extends connect(store)(LitElementExtended) {
  @property({ type: Object }) consentGroup?: ConsentGroup = undefined;
  @property({ type: Number }) groupIndex: number = -1;
  @property({ type: String }) mode: string = '';

  @state() generation: Generation = Generation.CLASSIC;

  constructor() {
    super();
    const { consents: { generation } } = store.getState();
    this.generation = generation ?? 1;
    this.addEventListener('occl.collapsible.open', () => {
      this.oneClickOpenHandler();
    });
  }

  oneClickOpenHandler() {
    service.tracker.doTracking(TRACKER_EVENT_NAME_CLICK_COLLAPSABLE, {
      group: this.groupName,
      subject: this.mode && this.mode.includes('multiedit') ? 'consent' : 'oneclick',
    });
    store.dispatch(setCollapsibleOpen(this.groupName));
  }

  createRenderRoot() {
    return this; // no shadow DOM for sub-components
  }

  updated() {
    const { oneclick: isOneclick, consents } = this.consentGroup as ConsentGroup;

    // calculate oneclick
    if (isOneclick && !this.mode.includes('multiedit')) {
      // calculate oneclick checkbox: true only if all granted
      const inGroupIsSomethingUnchecked = haveUncheckedFeatures(consents)
        || !!consents.find((c) => !c.granted);
      const oneclickCheck = this.querySelector(`#oneclick_check_${this.groupIndex}`) as HTMLInputElement;
      oneclickCheck.checked = !inGroupIsSomethingUnchecked;
    }

    this.replaceElements();
  }

  get noConsentSet() {
    return !(this.consentGroup as ConsentGroup).consents.find((co) => co.granted);
  }

  oneclickAction() {
    const oneclickCheck = this.querySelector(`#oneclick_check_${this.groupIndex}`) as HTMLInputElement;
    const { checked } = oneclickCheck;
    store.dispatch(oneclick(this.groupIndex, checked));
  }

  denyAction() {
    const denyCheck = this.querySelector(`#deny_check_${this.groupIndex}`) as HTMLInputElement;
    const { checked } = denyCheck;
    store.dispatch(deny(this.groupIndex, checked));
  }

  get groupOneclickText() {
    const checkBoxId = `oneclick_check_${this.groupIndex}`;

    const clickTextTemplate = this.getTemplateFn('clickTextTemplate');

    return clickTextTemplate(checkBoxId, this);
  }

  // eslint-disable-next-line sonarjs/function-return-type
  get groupText() {
    const { text } = this.consentGroup as ConsentGroup;

    return text ? html` ${unsafeHTML(text)} ` : nothing;
  }

  get groupName() {
    return this.consentGroup?.name;
  }

  get consentPrefix() {
    return this.consentGroup?.consentPrefix;
  }

  get groupOneclickConsentsContainer() {
    const { config: { stylecontext = '' }, consents: { collapsibleOpenGroupName } } = store.getState();

    const clickConsentsContainerTemplate = this.getTemplateFn('clickConsentsContainerTemplate');

    return clickConsentsContainerTemplate(stylecontext, this, collapsibleOpenGroupName);
  }

  groupConsents(layout = 'default') {
    const { consents, required, displayMode } = this.consentGroup as ConsentGroup;

    // "required" or displayMode="list" => render as list
    if (required || this.isDisplayModeList(displayMode)) {
      const groupConsentsTemplate = this.getTemplateFn('groupConsentsTemplate');
      return [groupConsentsTemplate(this)];
    }

    const displayConsents = consents.filter((consent) => !consent.deprecated && consent.modeconstraint !== 'view');
    return displayConsents.map((consent) => html`
      <tef-coms-consent
        .consent=${consent}
        ?required=${required}
        .layout=${layout}
        .displayMode=${displayMode}
        data-testid=${consent.id}>
      </tef-coms-consent>`);
  }

  get withdrawal() {
    const { denyall, oneclick: denyOneclick } = this.consentGroup as ConsentGroup;

    if (!denyall || denyOneclick) {
      return null;
    }

    const checkboxId = `deny_check_${this.groupIndex}`;

    const withdrawalTemplate = this.getTemplateFn('withdrawalTemplate');

    return withdrawalTemplate(this, checkboxId);
  }

  openGroupForEdit(e: MouseEvent) {
    store.dispatch(setActiveGroupInd(this.groupIndex));
    service.tracker.doTracking(TRACKER_EVENT_NAME_START_CHANGE_PERMISSION, {
      group: this.groupName,
    });
    e.preventDefault();
  }

  get viewTemplate() {
    const {
      consents: { consentGroupsView },
      config: { stylecontext = '' },
    } = store.getState();

    const { consents } = consentGroupsView![this.groupIndex];

    const filteredConsents = consents
      .filter(({ granted = false }) => granted === true)
      .filter(({ modeconstraint }) => modeconstraint !== 'edit');

    const groupViewTemplate = this.getTemplateFn('groupViewTemplate');

    return groupViewTemplate(this, consentGroupsView, filteredConsents, stylecontext);
  }

  get template() {
    return this.getTemplateFn('groupTemplate')(this);
  }

  render() {
    const isMulteditMode = this.mode && this.mode.includes('multiedit');

    if (isMulteditMode) {
      return this.viewTemplate;
    }

    return this.template;
  }
}
